import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ExternalLink } from 'lucide-react';
import { Button } from '@/marketing/components/ui/button';
import { CreatorScoreBadge } from '../score/CreatorScoreBadge';
import { getSocialMediaUrl, getPlatformLabel } from '@/marketing/lib/socialMediaUrls';
import type { CampaignInfluencer } from '@/marketing/models/campaignInfluencer';
import type { ScoutingDecision } from '@/marketing/models/campaignScouting';
import { isSelectableFinalist, scoutingStatus, type ScoutingViewRow } from './scoutingView';
import {
  BRAND_REVIEW_LABELS,
  displayScoutingDate,
  displayScoutingNumber,
  scoutingName,
  scoutingProductLabels,
} from './scoutingDetails';
import { ScoutingAccounts, ScoutingCoverage } from './ScoutingDetailCells';
import { ScoutingDecisionCell } from './ScoutingDecisionCell';

interface ScoutingTableProps {
  rows: ScoutingViewRow[];
  checked: Set<string>;
  disabled: boolean;
  onToggle: (id: string) => void;
  onDecide: (row: ScoutingViewRow, decision: ScoutingDecision) => void;
  onOpen: (creator: CampaignInfluencer, queue: CampaignInfluencer[]) => void;
  onProducts: (row: ScoutingViewRow) => void;
  onNotes: (row: ScoutingViewRow) => void;
  pageIndex?: number;
  totalMatching?: number | undefined;
}
const HEADERS = [
  'Select',
  'Creator',
  'Region',
  'Address',
  'Email',
  'Phone',
  'Followers',
  'Avg views',
  'Notes',
  'Product (LIST)',
  'Score',
  'Platform',
  'ER',
  'Posts (30 days)',
  'Last post',
  'Language',
  'Full name',
  'Scouting status',
  'Decision',
  'Review brand',
];
const CELL = 'tw-px-3 tw-py-2 tw-align-top';
export function ScoutingTable({
  rows,
  checked,
  disabled,
  onToggle,
  onDecide,
  onOpen,
  onProducts,
  onNotes,
  pageIndex = 0,
  totalMatching = rows.length,
}: ScoutingTableProps) {
  const table = useReactTable({
    data: rows,
    columns: HEADERS.map((header) => ({ id: header, header })),
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.influencerId,
    manualPagination: true,
    manualFiltering: true,
    rowCount: totalMatching,
    state: { pagination: { pageIndex, pageSize: 50 } },
  });
  const queue = rows.flatMap((row) => (row.creator ? [row.creator] : []));
  return (
    <div
      className="tw-max-h-[65vh] tw-min-h-52 tw-overflow-auto tw-rounded tw-border"
      tabIndex={0}
      aria-label="Scouting table scroll area"
    >
      <table className="tw-w-max tw-min-w-full tw-border-separate tw-border-spacing-0 tw-text-left tw-text-xs">
        <caption className="tw-sr-only">Campaign scouting decisions and finalists</caption>
        <thead>
          <tr>
            {HEADERS.map((label, index) => (
              <th
                key={label}
                scope="col"
                className={`tw-sticky tw-top-0 tw-z-20 tw-border-b tw-bg-slate-100 tw-px-3 tw-py-2 tw-whitespace-nowrap ${index === 1 ? 'tw-left-0 !tw-z-30 tw-min-w-40' : ''}`}
                title={
                  label === 'ER'
                    ? 'Last 10–12 unpinned posts; only verified sample metrics are shown.'
                    : label === 'Avg views'
                      ? 'Stored profile average views; sample period unavailable. Not a verified unpinned median.'
                      : undefined
                }
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.getRowModel().rows.map(({ original: row }) => {
            const name = row.creator?.handle ?? row.influencerId;
            const profile =
              row.creator?.platform && row.creator.handle
                ? getSocialMediaUrl(row.creator.platform, row.creator.handle)
                : '';
            const d = row.details;
            const contact = d?.contact;
            const metricsReason = d?.social.reason ?? 'Scouting details unavailable';
            return (
              <tr key={row.influencerId} className="[&>td]:tw-border-b">
                <td className={CELL}>
                  {isSelectableFinalist(row) && (
                    <input
                      type="checkbox"
                      aria-label={`Select finalist ${name}`}
                      checked={checked.has(row.influencerId)}
                      disabled={disabled}
                      onChange={() => onToggle(row.influencerId)}
                    />
                  )}
                </td>
                <td className={`${CELL} tw-sticky tw-left-0 tw-z-10 tw-min-w-40 tw-bg-background tw-shadow-sm`}>
                  <div className="tw-flex tw-items-center tw-gap-2">
                    {row.creator ? (
                      <button
                        type="button"
                        className="tw-max-w-40 tw-break-all tw-font-semibold hover:tw-text-blue-600 hover:tw-underline"
                        onClick={() => row.creator && onOpen(row.creator, queue)}
                      >
                        {row.creator.handle ? '@' : ''}
                        {name}
                      </button>
                    ) : (
                      <span>{name}</span>
                    )}
                    {profile && (
                      <a
                        href={profile}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Open profile for ${name}`}
                        className="tw-text-muted-foreground"
                      >
                        <ExternalLink className="tw-h-3.5 tw-w-3.5" />
                      </a>
                    )}
                  </div>
                  <ScoutingAccounts row={row} />
                  <ScoutingCoverage row={row} />
                </td>
                <td
                  className={`${CELL} tw-min-w-28 tw-max-w-28 tw-whitespace-normal`}
                  title={`Source: ${contact?.source ?? 'unavailable'}`}
                >
                  {[contact?.country, contact?.state, contact?.city].filter(Boolean).join(' · ') || '-'}
                </td>
                <td
                  className={`${CELL} tw-min-w-36 tw-max-w-44 tw-whitespace-pre-wrap tw-break-words`}
                  title={`Source: ${contact?.source ?? 'unavailable'}`}
                >
                  {[
                    contact?.address1,
                    contact?.address2,
                    [contact?.city, contact?.state, contact?.postcode].filter(Boolean).join(' '),
                    contact?.country,
                  ]
                    .filter(Boolean)
                    .join('\n') || '-'}
                </td>
                {(['email', 'phone'] as const).map((key) => (
                  <td
                    key={key}
                    className={`${CELL} tw-min-w-28 tw-max-w-32 tw-break-words`}
                    title={`Source: ${contact?.source ?? 'unavailable'}`}
                  >
                    {contact?.[key] || '-'}
                  </td>
                ))}
                <td
                  className={CELL}
                  title={`Stored ${d?.social.platform ?? 'profile'} observation · ${displayScoutingDate(d?.social.observedAt)}`}
                >
                  {d?.social.followers == null ? '-' : displayScoutingNumber(d.social.followers)}
                </td>
                <td
                  className={`${CELL} tw-min-w-24`}
                  title={`Stored profile average · sample period unavailable · observed ${displayScoutingDate(d?.social.observedAt)}`}
                >
                  {d?.social.profileAverageViews == null ? '-' : displayScoutingNumber(d.social.profileAverageViews)}
                  <div className="tw-mt-1 tw-text-[10px] tw-text-muted-foreground">Stored profile avg</div>
                </td>
                <td className={`${CELL} tw-min-w-40 tw-max-w-56`}>
                  <p className="tw-line-clamp-3 tw-whitespace-pre-wrap tw-break-words">
                    {row.notes ? row.notes.text || 'No notes' : 'Unavailable'}
                  </p>
                  {row.notes && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="tw-mt-1 tw-h-6 tw-px-2 tw-text-[11px]"
                      disabled={disabled}
                      onClick={() => onNotes(row)}
                      aria-label={`Edit notes for ${name}`}
                    >
                      Edit notes
                    </Button>
                  )}
                </td>
                <td className={`${CELL} tw-min-w-44`}>
                  <div className="tw-max-w-56 tw-whitespace-normal">
                    {scoutingProductLabels(d?.products).join(', ') || '—'}
                  </div>
                  {d?.products.editable && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="tw-mt-1 tw-h-6 tw-px-2 tw-text-[11px]"
                      disabled={disabled}
                      onClick={() => onProducts(row)}
                      aria-label={`Edit products for ${name}`}
                    >
                      Edit products
                    </Button>
                  )}
                </td>
                <td className={CELL}>
                  {row.creator ? (
                    <CreatorScoreBadge creator={row.creator} highlightLowScore={false} storedScore={d?.score ?? null} />
                  ) : (
                    displayScoutingNumber(d?.score?.finalScore)
                  )}
                </td>
                <td className={CELL}>
                  {row.creator?.platform ? getPlatformLabel(row.creator.platform) : (d?.social.platform ?? '—')}
                </td>
                <td className={CELL} title={metricsReason}>
                  {d?.social.engagementRate == null ? '—' : `${displayScoutingNumber(d.social.engagementRate)}%`}
                  {d?.social.profileEngagementRate != null && (
                    <div
                      className="tw-mt-1 tw-text-[10px] tw-text-muted-foreground"
                      title="Stored legacy profile formula uses followers; not the requested same-video views denominator."
                    >
                      Stored profile ER (%):{' '}
                      {d.social.profileEngagementRate.toLocaleString('en-US', { maximumFractionDigits: 6 })}
                    </div>
                  )}
                </td>
                <td className={CELL} title={metricsReason}>
                  {displayScoutingNumber(d?.social.posts30Days)}
                </td>
                <td className={`${CELL} tw-whitespace-nowrap`} title={metricsReason}>
                  {displayScoutingDate(d?.social.lastPostedAt)}
                </td>
                <td className={CELL}>{d?.social.languages.join(', ') || '—'}</td>
                <td className={CELL}>{scoutingName(row) || '—'}</td>
                <td className={`${CELL} tw-whitespace-nowrap`}>{scoutingStatus(row)}</td>
                <td className={CELL}>
                  <ScoutingDecisionCell row={row} disabled={disabled} onDecide={onDecide} />
                </td>
                <td
                  className={`${CELL} tw-min-w-36`}
                  title="Existing campaign pipeline status; no separate brand decision is inferred."
                >
                  {d ? BRAND_REVIEW_LABELS[d.brandReview.status] : 'Unavailable'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
